import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import 'typeface-rubik';
import oidc from 'oidc-client';
import workflowUtils from './utils/workflowUtils';
import contexts from './contexts';
import registrationApi from './api/registrationApi';
import App from './App';
import store from './store';
import startupConfig from './config/startupConfig';
import Auth from './api/auth';

import { initSession } from './lib/threatmetrix';
import '@shiftfinancial/design-system/style.css';

const AppWithUserLogin = () => {
  // removing query string params
  const urlWithoutQueryParams = window.location.href.split('?')[0];

  const [renderContent, setRenderContent] = useState(<Fragment />);

  function renderApp(user) {
    const loginStatus = {
      user,
    };

    const { LoginContext } = contexts;

    setRenderContent(
      <Provider store={store}>
        <LoginContext.Provider value={loginStatus}>
          {/* eslint-disable-next-line no-undef */}
          <BrowserRouter basename="/">
            <React.StrictMode>
              <App />
            </React.StrictMode>
          </BrowserRouter>
        </LoginContext.Provider>
      </Provider>,
    );
  }

  function loginUser(user) {
    Auth.loggedIn(user.access_token, user.expires_at, 'access_token');
  }

  function handleUserLogin(userManager) {
    userManager.getUser().then((user) => {
      if (!user) {
        userManager.signinRedirect();
      } else {
        loginUser(user);
        renderApp(user);
      }
    });
  }

  function handlePostLoginCallBack() {
    new oidc.UserManager()
      .signinRedirectCallback()
      .then(() => {
        const baseUrl = urlWithoutQueryParams.substring(
          0,
          urlWithoutQueryParams.lastIndexOf('callback'),
        );
        const isDevMode = window.location.href.includes('localhost');
        if (isDevMode) {
          window.location = `${baseUrl}?mode=rm`;
        } else {
          window.location = baseUrl;
        }
      })
      .catch((e) => {
        throw e;
      });
  }

  function init({ userManagerConfiguration, threatMetrixConfiguration }) {
    if (window.location.pathname.includes('callback')) {
      handlePostLoginCallBack();
      return;
    }

    initSession(threatMetrixConfiguration);

    const mode = workflowUtils.getRegistrationModeAndPortal();

    const userManagerConfig = {
      authority: userManagerConfiguration.authority,
      client_id: userManagerConfiguration.clientId,
      redirect_uri: `${urlWithoutQueryParams}/callback`,
      response_type: userManagerConfiguration.responseType,
      scope: userManagerConfiguration.scope,
      automaticSilentRenew: true,
    };

    const userManager = new oidc.UserManager(userManagerConfig);
    if (mode.registrationMode !== 'rm') {
      userManager.getUser().then((user) => {
        if (!user) {
          renderApp();
        } else {
          renderApp(user);
        }
      });
    } else {
      handleUserLogin(userManager);
    }
  }

  const fetchConfiguration = useCallback(async () => {
    const response = await registrationApi.getStartupConfig();
    startupConfig.set(response.data);
    init(response.data);
  }, []);

  useEffect(() => {
    fetchConfiguration();
  }, [fetchConfiguration]);

  return renderContent;
};

export default AppWithUserLogin;
